import { Box, Button, LinearProgress, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import type { JSX, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  calibrationCard,
  calibrationCardActions,
  warningCardHeader,
} from '@/utils/theme'
import CompositionCheckTable from '@/components/CompositionCheckTable'
import type { Composition } from '@/pages/calibration/composition'

interface Props {
  serial: string
  label?: string
  type: string
  isSaving: boolean
  composition: Composition | null
  onBack: () => void
  onFormSubmit: (e?: React.BaseSyntheticEvent) => void | Promise<void>
  children: ReactNode
}

const VerifyStepBase = ({
  serial,
  label,
  type,
  isSaving,
  composition,
  onBack,
  onFormSubmit,
  children,
}: Props): JSX.Element => {
  const { t } = useTranslation('common')
  const headerTitle = label ? `${serial} - ${label}` : serial

  return (
    <Box
      component="form"
      onSubmit={onFormSubmit}
      noValidate
      data-testid="verify-step-form"
    >
      <Box sx={calibrationCard}>
        <Box sx={warningCardHeader}>
          <Typography
            component="h4"
            sx={{
              color: '#fff',
              fontWeight: 300,
              fontSize: '18px',
              lineHeight: '1.375rem',
              margin: '0 0 5px 0',
            }}
          >
            {headerTitle}
          </Typography>
          <Typography
            component="h4"
            sx={{
              color: '#fff',
              fontWeight: 300,
              fontSize: '18px',
              lineHeight: '1.375rem',
              margin: 0,
            }}
          >
            {type}
          </Typography>
        </Box>

        {/* Card body */}
        <Box sx={{ padding: '0.9375rem 20px' }}>
          <Grid container>
            {/* Left column = breakpoints match v1 xs=12 sm=6 md=4 lg=3 xl=2
                Updated to md=5 lg=4 xl=3 for slightly wider feel as requested */}
            <Grid
              size={{ xs: 12, sm: 6, md: 5, lg: 4, xl: 3 }}
              sx={{ padding: '0 15px !important' }}
            >
              {children}
            </Grid>
            <Grid
              size={{ xs: 12, lg: 8, xl: 9 }}
              sx={{ padding: '0 15px !important' }}
            >
              <CompositionCheckTable composition={composition} />
            </Grid>
          </Grid>
        </Box>

        {isSaving && (
          <LinearProgress
            variant="indeterminate"
            color="primary"
            sx={{
              borderBottomLeftRadius: 1,
              borderBottomRightRadius: 1,
              mb: 0,
            }}
          />
        )}
      </Box>

      {/* Actions */}
      <Box sx={calibrationCardActions}>
        <Button
          variant="contained"
          onClick={onBack}
          disabled={isSaving}
          data-testid="verify-back-button"
          sx={{ marginTop: 2, marginRight: 2 }}
        >
          {t('general.back')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="success"
          disabled={isSaving}
          data-testid="verify-save-button"
          sx={{ marginTop: 2, marginRight: 2 }}
        >
          {t('form.submit')}
        </Button>
      </Box>
    </Box>
  )
}

export default VerifyStepBase
